import React, {
	memo,
	useCallback,
	useMemo,
	useState,
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextBox } from "uikit";

import MultiSelect from "../../LocalMultiSelect";
import { useTypedSelector } from "../../../../redux/store";
import { useObjectEditor } from "../../../../hooks";
import { generateAccessName, AccessKey, hasAccess } from "../../../../access";
import { useNotification } from "../../../NotificationProvider";
import Modal from "../../../Modal";
import {
	StyledColumn,
	StyledGrid,
	CompaniesAndTaxiServices,
	CheckBoxWithText,
	MultiSelectExecutorWithModal,
	Divider,
	MultiSelectOrderClassesWithModal,
	MultiSelectCounterparty,
	MultiSelectAgentWithModal,
	MultiSelectClientWithModal,
	MultiSelectServiceWithModal,
	MultiSelectCarClassWithModal,
	MultiSelectSectorWithModal,
	MultiSelectTransferWithModal,
	MultiSelectSourceCallSingWithModal,
} from "../..";

import { Header, Footer } from "./components";
import { applyOrderFilters } from "./utils";
import {
	ORDER_STATUSES_ENUM,
	ORDER_STATUSES,
	PAYMENTS_ENUM,
	PAYMENTS,
	DEFAUT_VALUEE_FILTERS,
} from "./constants";
import { ValueDefaultFilters } from "./constants/interface";

const StyledColumnWrap = styled(StyledColumn)`
	overflow: hidden;
	border-radius: 10px;
	background-color: white;
`;

const DividerCustomer = styled(Divider)`
	margin-top: 6px;
	margin-bottom: 6px;
`;

const ArchiveOrderFiltersModal: React.FC<ArchiveOrderFiltersModal.Props> = ({
	value,
	onChange,
	isOpenArchiveFilterModal,
	setOpenArchiveFilterModal,
	accessSection,
}): JSX.Element => {
	const _value = useMemo(() => value, [value]);
	const { t } = useTranslation();
	const { info } = useNotification();
	const isFirstRender = useRef(true);

	const lang = useTypedSelector((state) => state.session.language);
	const subscribeOptions = useMemo(
		() => ({
			lang,
		}),
		[lang],
	);
	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const accessBtnGroup = useMemo(() => {
		const accessKeys = [
			{
				key: "companiesAndTaxiServices",
				accessKey: AccessKey.COMPANIES_AND_TAXI_SERVICES,
			},
			{
				key: "archiveFilterExecutor",
				accessKey: AccessKey.ARCHIVE_FILTER_EXECUTOR,
			},
			{
				key: "archiveFilterStatusOrder",
				accessKey: AccessKey.ARCHIVE_FILTER_STATUS_ORDER,
			},
			{
				key: "archiveFilterNumberOrder",
				accessKey: AccessKey.ARCHIVE_FILTER_NUMBER_ORDER,
			},
			{
				key: "archiveFilterNumberPhone",
				accessKey: AccessKey.ARCHIVE_FILTER_NUMBER_PHONE,
			},
			{
				key: "archiveFilterStreet",
				accessKey: AccessKey.ARCHIVE_FILTER_STREET,
			},
			{
				key: "archiveFilterClient",
				accessKey: AccessKey.ARCHIVE_FILTER_CLIENT,
			},
			{
				key: "archiveFilterCounterparty",
				accessKey: AccessKey.ARCHIVE_FILTER_COUNTERPARTY,
			},
			{
				key: "archiveFilterAgent",
				accessKey: AccessKey.ARCHIVE_FILTER_AGENT,
			},
			{
				key: "archiveFilterPayments",
				accessKey: AccessKey.ARCHIVE_FILTER_PAYMENTS,
			},
			{
				key: "archiveFilterClasses",
				accessKey: AccessKey.ARCHIVE_FILTER_CLASSES,
			},
			{
				key: "archiveFilterServices",
				accessKey: AccessKey.ARCHIVE_FILTER_SERVICES,
			},
			{
				key: "archiveFilterSectors",
				accessKey: AccessKey.ARCHIVE_FILTER_SECTORS,
			},
			{
				key: "archiveFilterTransfer",
				accessKey: AccessKey.ARCHIVE_FILTER_TRANSFER,
			},
			{
				key: "archiveFilterSourceCallSign",
				accessKey: AccessKey.ARCHIVE_FILTER_SOURCE_CALL_SIGN,
			},
		];

		if (!accessSection) {
			return Object.fromEntries(accessKeys.map(({ key }) => [key, true]));
		}

		return Object.fromEntries(
			accessKeys.map(({ key, accessKey }) => [
				key,
				hasAccess(
					generateAccessName(...accessSection, accessKey),
					personalRole,
				),
			]),
		);
	}, [accessSection, personalRole]);

	const [valueModalFilter, setValueModalFilter] =
		useState<ArchiveOrderFiltersModal.Value>(_value);

	const onCloseModal = useCallback(() => {
		setOpenArchiveFilterModal(false);
	}, [setOpenArchiveFilterModal]);

	const gridSettings = useMemo(
		() => ({
			areas: "",
			gap: "10px",
			alignItems: "center",
			columns: "1fr 2fr",
			w: "100%",
		}),
		[],
	);

	const valueEditor = useObjectEditor(valueModalFilter, setValueModalFilter);

	const taxiServicesData = valueEditor.usePicker(
		["taxiServiceIds", "taxiServices", "companyIds"],
		"json",
	);
	const onChangeContent = valueEditor.useAssigner();

	const executorIds = valueEditor.useGetter("executorIds");
	const setExecutorIds = valueEditor.useSetter("executorIds");
	const checkBoxExecutorIds = valueEditor.useGetter("checkBoxExecutorIds");
	const setCheckBoxExecutorIds = valueEditor.useSetter("checkBoxExecutorIds");

	const statusOrder = valueEditor.useGetter("statusOrder");
	const setStatusOrder = valueEditor.useSetter("statusOrder");
	const checkBoxStatusOrder = valueEditor.useGetter("checkBoxStatusOrder");
	const setCheckBoxStatusOrder = valueEditor.useSetter("checkBoxStatusOrder");

	const numberOrder = valueEditor.useGetter("numberOrder");
	const setNumberOrder = valueEditor.useSetter("numberOrder");
	const checkNumberOrder = valueEditor.useGetter("checkNumberOrder");
	const setCheckNumberOrder = valueEditor.useSetter("checkNumberOrder");

	const numberPhone = valueEditor.useGetter("numberPhone");
	const setNumberPhone = valueEditor.useSetter("numberPhone");
	const checkNumberPhone = valueEditor.useGetter("checkNumberPhone");
	const setCheckNumberPhone = valueEditor.useSetter("checkNumberPhone");

	const street = valueEditor.useGetter("street");
	const setStreet = valueEditor.useSetter("street");
	const checkStreet = valueEditor.useGetter("checkStreet");
	const setCheckStreet = valueEditor.useSetter("checkStreet");

	const agent = valueEditor.useGetter("agent");
	const setAgent = valueEditor.useSetter("agent");
	const checkAgent = valueEditor.useGetter("checkAgent");
	const setCheckAgent = valueEditor.useSetter("checkAgent");

	const client = valueEditor.useGetter("client");
	const setClient = valueEditor.useSetter("client");
	const checkClient = valueEditor.useGetter("checkClient");
	const setCheckClient = valueEditor.useSetter("checkClient");

	const counterparty = valueEditor.useGetter("counterparty");
	const setCounterparty = valueEditor.useSetter("counterparty");
	const checkCounterparty = valueEditor.useGetter("checkCounterparty");
	const setCheckCounterparty = valueEditor.useSetter("checkCounterparty");

	const classes = valueEditor.useGetter("classes");
	const setClasses = valueEditor.useSetter("classes");
	const checkClasses = valueEditor.useGetter("checkClasses");
	const setCheckClasses = valueEditor.useSetter("checkClasses");

	const services = valueEditor.useGetter("services");
	const setServices = valueEditor.useSetter("services");
	const checkServices = valueEditor.useGetter("checkServices");
	const setCheckServices = valueEditor.useSetter("checkServices");

	const sectors = valueEditor.useGetter("sectors");
	const setSectors = valueEditor.useSetter("sectors");
	const checkSectors = valueEditor.useGetter("checkSectors");
	const setCheckSectors = valueEditor.useSetter("checkSectors");

	const payments = valueEditor.useGetter("payments");
	const setPayments = valueEditor.useSetter("payments");
	const checkPayments = valueEditor.useGetter("checkPayments");
	const setCheckPayments = valueEditor.useSetter("checkPayments");

	const transfer = valueEditor.useGetter("transfer");
	const setTransfer = valueEditor.useSetter("transfer");
	const checkTransfer = valueEditor.useGetter("checkTransfer");
	const setCheckTransfer = valueEditor.useSetter("checkTransfer");

	const sourceCallSing = valueEditor.useGetter("sourceCallSing");
	const setSourceCallSing = valueEditor.useSetter("sourceCallSing");
	const checkSourceCallSing = valueEditor.useGetter("checkSourceCallSing");
	const setCheckSourceCallSing = valueEditor.useSetter("checkSourceCallSing");

	const textTranslates: Record<string, string> = useMemo(() => {
		const getTranslation = (key: string) =>
			t(`pages.mainPage.pages.archives.tabs.orders.filter.${key}`) ?? "";

		return {
			// Select
			unclosedOrders: getTranslation("statusOrderOptions.unclosedOrders"),
			completedOrders: getTranslation(
				"statusOrderOptions.completedOrders",
			),
			noCarAvailable: getTranslation("statusOrderOptions.noCarAvailable"),
			clientRefusal: getTranslation("statusOrderOptions.clientRefusal"),
			driverRefusal: getTranslation("statusOrderOptions.driverRefusal"),
			calculationError: getTranslation(
				"statusOrderOptions.calculationError",
			),
			dispatcherError: getTranslation(
				"statusOrderOptions.dispatcherError",
			),
			// btn
			reset: getTranslation("reset"),
			ok: t("ok") ?? "",
			// label
			executor: getTranslation("executor"),
			statusOrder: getTranslation("statusOrder"),
			allTextStatusOrder: getTranslation("allTextStatusOrder"),
			allActiveTextStatusOrder: getTranslation(
				"allActiveTextStatusOrder",
			),
			numberOrder: getTranslation("numberOrder"),
			numberPhone: getTranslation("numberPhone"),
			street: getTranslation("street"),
			apply: getTranslation("apply"),
			cancel: getTranslation("cancel"),
			agent: getTranslation("agent"),
			client: getTranslation("client"),
			counterparty: getTranslation("counterparty"),
			classes: getTranslation("classes"),
			services: getTranslation("services"),
			sectors: getTranslation("sectors"),
			payments: getTranslation("payments"),
			streetChoise: t("streetChoise") ?? "",
			numberPhoneChoise: t("numberPhoneChoise") ?? "",
			executorChoise: t("executorChoise") ?? "",
			statusOrderChoise: t("statusOrderChoise") ?? "",
			numberOrderChoise: t("numberOrderChoise") ?? "",
			paymentsChoise: t("paymentsChoise") ?? "",
			title: getTranslation("title"),
			transfer: getTranslation("transfer"),
			customerCallSignCreated: getTranslation("customerCallSignCreated"),
			transferChoise: t("transferChoise") ?? "",
			customerCallSingChoise: t("customerCallSingChoise") ?? "",
			titleInfo: t("notification.title.info") ?? "",
			descriptionInfo:
				t("notification.description.filtersClearedByBranchChange") ??
				"",
		};
	}, [t]);

	const handleClearBtn = useCallback(() => {
		setValueModalFilter(DEFAUT_VALUEE_FILTERS);
	}, []);

	const handleCancelBtn = useCallback(() => {
		setValueModalFilter(_value);
		onCloseModal();
	}, [onCloseModal, _value]);

	const handleApplyBtn = useCallback(() => {
		onChange(valueModalFilter);
		onCloseModal();
	}, [onChange, onCloseModal, valueModalFilter]);

	const optionsStatusOrder = useMemo(() => {
		const options = ORDER_STATUSES.map((option, index) => ({
			key: index,
			label: textTranslates?.[option] ?? "",
			value: index,
			name: textTranslates?.[option] ?? "",
			data: { id: index, label: textTranslates?.[option] ?? "" },
		}));

		return options || [];
	}, [textTranslates]);

	const paymentsOptions = useMemo(
		() =>
			PAYMENTS.map((payment, index) => ({
				key: index,
				label:
					t(
						`pages.mainPage.pages.archives.tabs.orders.filter.paymentsOptions.${payment}`,
					) ?? payment,
				value: index,
			})),
		[t],
	);

	const handlePaymentsFilter = useCallback(
		(value) => {
			setPayments(value);
		},
		[setPayments],
	);

	const disabledFromEmptyTaxyServices = useMemo(
		() => taxiServicesData.taxiServiceIds.length === 0,
		[taxiServicesData.taxiServiceIds.length],
	);

	const clearValueAfterChangeTaxiServices = useCallback(() => {
		const cleared: string[] = [];

		const filters = [
			{
				name: textTranslates.executor,
				value: executorIds,
				defaultValue: DEFAUT_VALUEE_FILTERS.executorIds,
				setValue: setExecutorIds,
				check: checkBoxExecutorIds,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkBoxExecutorIds,
				setCheck: setCheckBoxExecutorIds,
			},
			{
				name: textTranslates.client,
				value: client,
				defaultValue: DEFAUT_VALUEE_FILTERS.client,
				setValue: setClient,
				check: checkClient,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkClient,
				setCheck: setCheckClient,
			},
			{
				name: textTranslates.counterparty,
				value: counterparty,
				defaultValue: DEFAUT_VALUEE_FILTERS.counterparty,
				setValue: setCounterparty,
				check: checkCounterparty,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkCounterparty,
				setCheck: setCheckCounterparty,
			},
			{
				name: textTranslates.agent,
				value: agent,
				defaultValue: DEFAUT_VALUEE_FILTERS.agent,
				setValue: setAgent,
				check: checkAgent,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkAgent,
				setCheck: setCheckAgent,
			},
			{
				name: textTranslates.payments,
				value: payments,
				defaultValue: DEFAUT_VALUEE_FILTERS.payments,
				setValue: setPayments,
				check: checkPayments,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkPayments,
				setCheck: setCheckPayments,
			},
			{
				name: textTranslates.services,
				value: services,
				defaultValue: DEFAUT_VALUEE_FILTERS.services,
				setValue: setServices,
				check: checkServices,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkServices,
				setCheck: setCheckServices,
			},
			{
				name: textTranslates.sectors,
				value: sectors,
				defaultValue: DEFAUT_VALUEE_FILTERS.sectors,
				setValue: setSectors,
				check: checkSectors,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkSectors,
				setCheck: setCheckSectors,
			},
			{
				name: textTranslates.customerCallSignCreated,
				value: sourceCallSing,
				defaultValue: DEFAUT_VALUEE_FILTERS.sourceCallSing,
				setValue: setSourceCallSing,
				check: checkSourceCallSing,
				defaultCheck: DEFAUT_VALUEE_FILTERS.checkSourceCallSing,
				setCheck: setCheckSourceCallSing,
			},
		];

		filters.forEach((f) => {
			const isValueEqual =
				JSON.stringify(f.value) === JSON.stringify(f.defaultValue);
			const isCheckEqual = f.check === f.defaultCheck;

			if (!isValueEqual || !isCheckEqual) {
				f.setValue(f.defaultValue);
				f.setCheck(f.defaultCheck);
				cleared.push(f.name);
			}
		});

		if (cleared.length > 0) {
			info(
				textTranslates.titleInfo,
				`${textTranslates.descriptionInfo}: ${cleared.join(", ")}`,
			);
		}
	}, [
		textTranslates.executor,
		textTranslates.client,
		textTranslates.counterparty,
		textTranslates.agent,
		textTranslates.payments,
		textTranslates.services,
		textTranslates.sectors,
		textTranslates.customerCallSignCreated,
		textTranslates.titleInfo,
		textTranslates.descriptionInfo,
		executorIds,
		setExecutorIds,
		checkBoxExecutorIds,
		setCheckBoxExecutorIds,
		client,
		setClient,
		checkClient,
		setCheckClient,
		counterparty,
		setCounterparty,
		checkCounterparty,
		setCheckCounterparty,
		agent,
		setAgent,
		checkAgent,
		setCheckAgent,
		payments,
		setPayments,
		checkPayments,
		setCheckPayments,
		services,
		setServices,
		checkServices,
		setCheckServices,
		sectors,
		setSectors,
		checkSectors,
		setCheckSectors,
		sourceCallSing,
		setSourceCallSing,
		checkSourceCallSing,
		setCheckSourceCallSing,
		info,
	]);

	useEffect(() => {
		if (!isOpenArchiveFilterModal) {
			return;
		}

		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		clearValueAfterChangeTaxiServices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taxiServicesData.taxiServiceIds]);

	return (
		<>
			{isOpenArchiveFilterModal && (
				<Modal dimmed>
					<StyledColumnWrap
						w="650px"
						h="76%"
						position="relative"
						p="6px 12px"
					>
						<Header
							title={textTranslates.title}
							onClose={handleCancelBtn}
						/>
						<StyledColumn
							w="100%"
							h="100%"
							p="1rem"
							justify="start"
							alignItems="center"
							gap="10px"
							overY="auto"
						>
							<StyledGrid {...gridSettings}>
								{accessBtnGroup.companiesAndTaxiServices && (
									<CompaniesAndTaxiServices
										value={taxiServicesData}
										onChange={onChangeContent}
										autoSelectAllTaxiServices={true}
									/>
								)}
							</StyledGrid>
							<DividerCustomer side="top" />
							<StyledColumn gap="10px" w="100%">
								{accessBtnGroup.archiveFilterExecutor && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.executor}
											value={checkBoxExecutorIds}
											onChange={setCheckBoxExecutorIds}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectExecutorWithModal
											value={executorIds}
											onChange={setExecutorIds}
											titleText={
												textTranslates.executorChoise
											}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											disabled={!checkBoxExecutorIds}
											sort={{ sortType: "asc" }}
											isFilterAccessCarParkFromUser
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterStatusOrder && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.statusOrder}
											value={checkBoxStatusOrder}
											onChange={setCheckBoxStatusOrder}
										/>

										<MultiSelectOrderClassesWithModal
											value={statusOrder}
											onChange={setStatusOrder}
											titleText={
												textTranslates.statusOrderChoise
											}
											allText={
												textTranslates.allTextStatusOrder
											}
											allActiveText={
												textTranslates.allActiveTextStatusOrder
											}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											selectOptions={optionsStatusOrder}
											disabled={!checkBoxStatusOrder}
											sort={{ sortType: "asc" }}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterNumberOrder && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.numberOrder}
											value={checkNumberOrder}
											onChange={setCheckNumberOrder}
										/>
										<TextBox.TextBox
											value={numberOrder}
											placeholder={
												textTranslates.numberOrderChoise
											}
											onChange={(val) => {
												const filteredValue =
													val.replace(
														/[^0-9, ]/g,
														"",
													);
												setNumberOrder(filteredValue);
											}}
											disabled={!checkNumberOrder}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterNumberPhone && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.numberPhone}
											value={checkNumberPhone}
											onChange={setCheckNumberPhone}
										/>
										<TextBox.TextBox
											value={numberPhone}
											placeholder={
												textTranslates.numberPhoneChoise
											}
											onChange={setNumberPhone}
											disabled={!checkNumberPhone}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterStreet && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.street}
											value={checkStreet}
											onChange={setCheckStreet}
										/>
										<TextBox.TextBox
											value={street}
											placeholder={
												textTranslates.streetChoise
											}
											onChange={setStreet}
											disabled={!checkStreet}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterClient && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.client}
											value={checkClient}
											onChange={setCheckClient}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectClientWithModal
											value={client}
											onChange={setClient}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											sort={{ sortType: "asc" }}
											disabled={!checkClient}
											taxiServiceIds={
												taxiServicesData.taxiServiceIds
											}
											isOnCheckBox={checkClient}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterCounterparty && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.counterparty}
											value={checkCounterparty}
											onChange={setCheckCounterparty}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectCounterparty
											value={counterparty}
											onChange={setCounterparty}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											sort={{ sortType: "asc" }}
											subscribeOptions={subscribeOptions}
											canUseCompanyIds={false}
											disabled={!checkCounterparty}
											canShowBillText={true}
											canShowCardText={true}
											taxiServiceIds={
												taxiServicesData.taxiServiceIds
											}
											isOnCheckBox={checkCounterparty}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterAgent && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.agent}
											value={checkAgent}
											onChange={setCheckAgent}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectAgentWithModal
											value={agent}
											onChange={setAgent}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											sort={{ sortType: "asc" }}
											subscribeOptions={subscribeOptions}
											disabled={!checkAgent}
											taxiServiceIds={
												taxiServicesData.taxiServiceIds
											}
											isOnCheckBox={checkAgent}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterPayments && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.payments}
											value={checkPayments}
											onChange={setCheckPayments}
										/>

										<MultiSelect
											value={payments}
											all
											placeholder={
												textTranslates.paymentsChoise
											}
											options={paymentsOptions}
											onChange={handlePaymentsFilter}
											disabled={!checkPayments}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterClasses && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.classes}
											value={checkClasses}
											onChange={setCheckClasses}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectCarClassWithModal
											value={classes}
											onChange={setClasses}
											sort={{ sortType: "asc" }}
											disabled={!checkClasses}
											titleText={
												t("carClassChoise") ?? ""
											}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterServices && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.services}
											value={checkServices}
											onChange={setCheckServices}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectServiceWithModal
											value={services}
											onChange={setServices}
											sort={{ sortType: "asc" }}
											disabled={!checkServices}
											titleText={
												t("servicesChoise") ?? ""
											}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterSectors && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.sectors}
											value={checkSectors}
											onChange={setCheckSectors}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>
										<MultiSelectSectorWithModal
											value={sectors}
											onChange={setSectors}
											titleText={
												t(
													"pages.settings.pages.tariffs.tabs.auto.modal.content.tabs.main.rules.modal.content.sectors.str200",
												) ?? ""
											}
											disabled={!checkSectors}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											taxiServiceIds={
												taxiServicesData.taxiServiceIds
											}
											isOnCheckBox={checkSectors}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterTransfer && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={textTranslates.transfer}
											value={checkTransfer}
											onChange={setCheckTransfer}
										/>

										<MultiSelectTransferWithModal
											value={transfer}
											onChange={setTransfer}
											titleText={
												textTranslates.transferChoise
											}
											disabled={!checkTransfer}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
										/>
									</StyledGrid>
								)}
								{accessBtnGroup.archiveFilterSourceCallSign && (
									<StyledGrid {...gridSettings}>
										<CheckBoxWithText
											title={
												textTranslates.customerCallSignCreated
											}
											value={checkSourceCallSing}
											onChange={setCheckSourceCallSing}
											disabled={
												disabledFromEmptyTaxyServices
											}
										/>

										<MultiSelectSourceCallSingWithModal
											value={sourceCallSing}
											onChange={setSourceCallSing}
											titleText={
												textTranslates.customerCallSingChoise
											}
											disabled={!checkSourceCallSing}
											modalSetting={{
												zIndex: 999,
												containerId: "root",
											}}
											taxiServiceIds={
												taxiServicesData.taxiServiceIds
											}
											isOnCheckBox={checkSourceCallSing}
										/>
									</StyledGrid>
								)}
							</StyledColumn>
						</StyledColumn>

						<Footer
							texts={{
								reset: textTranslates.reset,
								cancel: textTranslates.cancel,
								apply: textTranslates.apply,
							}}
							onReset={handleClearBtn}
							onCancel={handleCancelBtn}
							onApply={handleApplyBtn}
						/>
					</StyledColumnWrap>
				</Modal>
			)}
		</>
	);
};

declare namespace ArchiveOrderFiltersModal {
	interface Props {
		value: Value;
		onChange: (value: Value) => void;
		isOpenArchiveFilterModal: boolean;
		setOpenArchiveFilterModal: Dispatch<SetStateAction<boolean>>;
		accessSection?: AccessKey[];
	}

	interface Value extends ValueDefaultFilters {}
}

export {
	applyOrderFilters,
	ORDER_STATUSES_ENUM,
	ORDER_STATUSES,
	PAYMENTS_ENUM,
	PAYMENTS,
	DEFAUT_VALUEE_FILTERS,
};

export const ArchiveOrderFiltersModalMemo = memo(ArchiveOrderFiltersModal);
export default ArchiveOrderFiltersModal;
